import { useEffect, useMemo, useState, type KeyboardEvent } from "react"
import { router } from "@inertiajs/react"
import { Dialog, DialogHeader, DialogBody, DialogFooter } from "@/components/ui/dialog"
import type { Topic } from "@/types/topic"

type Props = {
  topic: Topic | null              // null when creating a new draft
  agentId: number                  // currently-selected agent
  tagSuggestions: string[]         // distinct tags across this agent's catalog (autocomplete source)
  isDraft: boolean
  onSaved: () => void
  onCancel: () => void
  onDelete: () => void
}

const BLANK = { name: "", key: "", description: "" }
const DESCRIPTION_MAX = 120

function slugify(input: string): string {
  return input.toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "")
}

function normalizeTag(input: string): string {
  return input.trim().toLowerCase().replace(/[^a-z0-9_]+/g, "_").replace(/^_+|_+$/g, "")
}

export function TopicForm({ topic, agentId, tagSuggestions, isDraft, onSaved, onCancel, onDelete }: Props) {
  const [fields, setFields] = useState(BLANK)
  const [submitting, setSubmitting] = useState(false)
  const [keyTouched, setKeyTouched] = useState(false)
  const [tags, setTags] = useState<string[]>([])
  const [tagDraft, setTagDraft] = useState("")
  const [tagsHelpOpen, setTagsHelpOpen] = useState(false)

  useEffect(() => {
    if (topic) {
      setFields({
        name:        topic.name,
        key:         topic.key,
        description: topic.description ?? "",
      })
      setKeyTouched(true)
      setTags(Array.isArray(topic.tags) ? [...topic.tags] : [])
    } else {
      setFields(BLANK)
      setKeyTouched(false)
      setTags([])
    }
    setTagDraft("")
  }, [topic?.id, isDraft])

  const keyImmutable = !isDraft && !!topic?.keyImmutable

  const addTag = (raw: string) => {
    const t = normalizeTag(raw)
    if (!t || tags.includes(t)) { setTagDraft(""); return }
    setTags((prev) => [...prev, t])
    setTagDraft("")
  }

  const removeTag = (t: string) => setTags((prev) => prev.filter((x) => x !== t))

  const handleNameChange = (val: string) => {
    setFields((f) => ({ ...f, name: val, key: keyTouched ? f.key : slugify(val) }))
  }

  const onTagKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault()
      if (tagDraft.trim()) addTag(tagDraft)
    } else if (e.key === "Backspace" && tagDraft === "" && tags.length > 0) {
      removeTag(tags[tags.length - 1])
    }
  }

  const filteredSuggestions = useMemo(() => {
    const q = tagDraft.trim().toLowerCase()
    if (!q) return []
    return tagSuggestions.filter((s) => s.includes(q) && !tags.includes(s)).slice(0, 6)
  }, [tagDraft, tagSuggestions, tags])

  const previewText = fields.name
    ? `${fields.name}${fields.description ? ` — ${fields.description}` : ""}`
    : "(name required)"

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setSubmitting(true)
    const payload = {
      topic: {
        agent_id:    agentId,
        name:        fields.name,
        key:         fields.key,
        description: fields.description,
        tags:        tags,
      }
    }
    const onFinish = () => { setSubmitting(false); onSaved() }
    if (isDraft || !topic) {
      router.post("/topics", payload, { preserveScroll: true, onFinish })
    } else {
      router.patch(`/topics/${topic.id}`, payload, { preserveScroll: true, onFinish })
    }
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-col rounded-card border border-border bg-app-panel">
      <div className="border-b border-border p-4">
        <div className="text-sm font-medium text-app-text">{isDraft ? "New Topic" : "Edit Topic"}</div>
        <div className="text-xs text-app-muted">
          {isDraft ? "Authoring a new label for this agent's catalog." : "Edit the selected topic."}
        </div>
      </div>

      <div className="space-y-4 overflow-y-auto p-4">
        <div>
          <label className="mb-1 block text-xs uppercase tracking-wide text-app-muted">Name</label>
          <input type="text" required value={fields.name}
            onChange={(e) => handleNameChange(e.target.value)}
            placeholder="Payouts & cashout"
            className="w-full rounded-control border border-border bg-app-bg px-3 py-2 text-sm text-app-text focus:border-brand-red focus:outline-none" />
          <p className="mt-1 text-xs text-app-muted">Shown in the help menu and in admin lists.</p>
        </div>

        <div>
          <label className="mb-1 flex items-center justify-between text-xs uppercase tracking-wide text-app-muted">
            <span>Key (slug)</span>
            {keyImmutable && (
              <span className="normal-case text-[11px] text-vip-goldSoft">🔒 locked after first save</span>
            )}
          </label>
          <input type="text" required value={fields.key}
            onChange={(e) => { setKeyTouched(true); setFields((f) => ({ ...f, key: e.target.value })) }}
            disabled={keyImmutable}
            pattern="[a-z0-9_]+"
            placeholder="payouts_cashout"
            className="w-full rounded-control border border-border bg-app-bg px-3 py-2 font-mono text-sm text-app-text focus:border-brand-red focus:outline-none disabled:cursor-not-allowed disabled:bg-app-panel disabled:text-app-muted" />
          <p className="mt-1 text-xs text-app-muted">
            {keyImmutable
              ? "Slug is locked. Delete and recreate to rename."
              : "Lowercase letters, digits, underscores. Used internally by the classifier."}
          </p>
        </div>

        <div>
          <label className="mb-1 flex items-center justify-between text-xs uppercase tracking-wide text-app-muted">
            <span>Description</span>
            <span className="normal-case text-[11px] text-app-muted">{fields.description.length} / {DESCRIPTION_MAX}</span>
          </label>
          <textarea rows={2} maxLength={DESCRIPTION_MAX} value={fields.description}
            onChange={(e) => setFields((f) => ({ ...f, description: e.target.value }))}
            placeholder="Cashing out, payout statuses, denials."
            className="w-full rounded-control border border-border bg-app-bg px-3 py-2 text-sm text-app-text focus:border-brand-red focus:outline-none" />
          <p className="mt-1 text-xs text-app-muted italic">
            Preview: <span className="text-app-text">{previewText}</span>
          </p>
        </div>

        <div className="border-t border-border pt-4">
          <label className="mb-1 flex items-center justify-between text-xs uppercase tracking-wide text-app-muted">
            <span>Tags</span>
            <button type="button" onClick={() => setTagsHelpOpen(true)}
              aria-label="How tags work"
              title="How tags work"
              className="inline-flex h-5 w-5 items-center justify-center rounded-full border border-border text-[11px] normal-case text-app-muted transition hover:border-brand-red hover:text-app-text">
              i
            </button>
          </label>
          <div className="flex flex-wrap items-center gap-1.5 rounded-control border border-border bg-app-bg p-2">
            {tags.map((t) => (
              <span key={t} className="inline-flex items-center gap-1 rounded-control border border-border bg-app-panel px-2 py-0.5 text-xs text-app-text">
                {t}
                <button type="button" onClick={() => removeTag(t)} aria-label={`Remove ${t}`}
                  className="opacity-70 transition hover:opacity-100">✕</button>
              </span>
            ))}
            <input
              type="text"
              value={tagDraft}
              onChange={(e) => setTagDraft(e.target.value)}
              onKeyDown={onTagKeyDown}
              onBlur={() => tagDraft.trim() && addTag(tagDraft)}
              placeholder={tags.length === 0 ? "Add tags (Enter or , to add)" : ""}
              className="min-w-[120px] flex-1 bg-transparent px-1 py-0.5 text-sm text-app-text placeholder:text-app-muted focus:outline-none"
            />
          </div>
          {filteredSuggestions.length > 0 && (
            <div className="mt-1 flex flex-wrap gap-1">
              <span className="text-[11px] text-app-muted">Suggestions:</span>
              {filteredSuggestions.map((s) => (
                <button key={s} type="button" onClick={() => addTag(s)}
                  className="rounded-control border border-border bg-app-bg px-1.5 py-0.5 text-[11px] text-app-muted transition hover:border-brand-red hover:text-app-text">
                  {s}
                </button>
              ))}
            </div>
          )}
          <p className="mt-1 text-xs text-app-muted">
            Free-form metadata. Skills query topics by tag at runtime.{" "}
            <button type="button" onClick={() => setTagsHelpOpen(true)} className="underline decoration-dotted hover:text-app-text">
              See how
            </button>
          </p>
        </div>
      </div>

      <div className="flex items-center justify-between border-t border-border p-4">
        <div>
          {!isDraft && topic && (
            <button type="button" onClick={onDelete}
              className="rounded-control border border-status-error/40 bg-transparent px-3 py-2 text-xs font-medium text-status-error transition hover:bg-status-error/10">
              🗑 Delete
            </button>
          )}
        </div>
        <div className="flex gap-2">
          <button type="button" onClick={onCancel}
            className="rounded-control border border-border px-4 py-2 text-sm text-app-text transition hover:bg-white/5">
            Cancel
          </button>
          <button type="submit" disabled={submitting}
            className="rounded-control border border-brand-red bg-brand-red px-4 py-2 text-sm font-medium text-white transition hover:bg-brand-redDark disabled:opacity-60">
            {submitting ? "Saving…" : "Save changes"}
          </button>
        </div>
      </div>

      <Dialog open={tagsHelpOpen} onClose={() => setTagsHelpOpen(false)} size="lg">
        <DialogHeader
          title="How tags work"
          subtitle="Free-form labels. The platform stores them; your skills decide what they mean."
        />
        <DialogBody>
          <section>
            <h3 className="mb-1 text-sm font-semibold text-app-text">What they are</h3>
            <p className="text-sm text-app-muted">
              Tags are arbitrary strings attached to a topic — <span className="font-mono">billing</span>,{" "}
              <span className="font-mono">vip</span>, <span className="font-mono">show_in_help_tool</span>,
              whatever you want. The platform doesn't read any tag by default. You decide which tags exist
              and what they trigger by writing a skill that filters topics by tag at runtime.
            </p>
          </section>

          <section>
            <h3 className="mb-1 text-sm font-semibold text-app-text">Querying tags from a skill</h3>
            <p className="mb-2 text-sm text-app-muted">
              Every agent has the universal <span className="font-mono">list_topics</span> tool. Use it from
              your skill's <span className="font-mono">prompt_md</span>:
            </p>
            <pre className="overflow-x-auto rounded-control border border-border bg-app-bg p-3 font-mono text-xs text-app-text">{`list_topics()                                  # all topics
list_topics(tag: "billing")                    # one tag
list_topics(tags: ["billing", "vip"])          # AND-match
list_topics(search: "payout")                  # name/description substring`}</pre>
            <p className="mt-2 text-xs text-app-muted">
              Returns <span className="font-mono">{`{ topics: [{ key, name, description, tags }, ...] }`}</span>.
            </p>
          </section>

          <section>
            <h3 className="mb-1 text-sm font-semibold text-app-text">Surfacing topics in a help menu</h3>
            <p className="mb-2 text-sm text-app-muted">
              There's no platform-level help menu — you build it inside a skill. Pick whatever tag name fits
              your agent (e.g. <span className="font-mono">show_in_help_tool</span>), tag the topics you want
              to advertise, then write a skill that calls{" "}
              <span className="font-mono">list_topics(tag: "show_in_help_tool")</span> and reads the names back to the user.
            </p>
            <p className="text-xs text-app-muted">
              Example skill <span className="font-mono">prompt_md</span> snippet:
            </p>
            <pre className="mt-1 overflow-x-auto rounded-control border border-border bg-app-bg p-3 font-mono text-xs text-app-text">{`When the user asks "what can you help with?", call
list_topics(tag: "show_in_help_tool") and reply:

  I can help with:
  - {name} — {description}
  - ...

Then ask what they need.`}</pre>
          </section>

          <section>
            <h3 className="mb-1 text-sm font-semibold text-app-text">Other uses</h3>
            <ul className="ml-4 list-disc space-y-1 text-sm text-app-muted">
              <li>Route topics to specific tools — tag <span className="font-mono">needs_human_review</span>{" "}
                and have a skill check that tag before resolving.</li>
              <li>Group topics by domain — <span className="font-mono">billing</span>,{" "}
                <span className="font-mono">account</span>, <span className="font-mono">offers</span> — and let the agent fan out per group.</li>
              <li>Mark topics for analytics — <span className="font-mono">priority_high</span>,{" "}
                <span className="font-mono">churn_risk</span>. Read them off resolved convos via the assignments table.</li>
            </ul>
          </section>

          <section>
            <h3 className="mb-1 text-sm font-semibold text-app-text">Conventions</h3>
            <p className="text-sm text-app-muted">
              The dashboard normalizes tags to lowercase snake_case on save. Use names that read well in your
              skill's prompt — they're shown to the LLM verbatim.
            </p>
          </section>
        </DialogBody>
        <DialogFooter>
          <button type="button" onClick={() => setTagsHelpOpen(false)}
            className="rounded-control border border-border px-4 py-2 text-sm text-app-text transition hover:bg-white/5">
            Got it
          </button>
        </DialogFooter>
      </Dialog>
    </form>
  )
}
